import { createContext, createElement, ReactNode, useContext, useMemo } from "react";

import { useInfo } from "@/context/InfoProvider/InfoProvider";

import { Config } from "./config";
import { Currency } from "./currency";
import { NumberAbbreviator } from "./numberAbbreviator";
import { ConfigContextType, ConfigData } from "./types";

// Context for config
const ConfigContext = createContext<ConfigContextType | null>(null);
const RETURN_CURRENCY = "RETURN";

// Hook to access the config context
export const useConfig = (): ConfigContextType => {
  const context = useContext(ConfigContext);
  if (!context) {
    throw new Error("useConfig must be used within a ConfigProvider");
  }
  return context;
};

interface ConfigProviderProps {
  children: ReactNode;
}

export const ConfigProvider: React.FC<ConfigProviderProps> = ({ children }) => {
  const { configData, currentOrganization, loading } = useInfo();

  const mergedConfig = {
    ...configData,
    config: {
      ...configData?.config,
      marketingLayer: currentOrganization?.configuration?.marketingLayer || {
        upperLayer: "",
        middleLayer: "",
        lowerLayer: "",
      },
      showReturnInsteadOfRevenue: currentOrganization?.configuration?.currency === RETURN_CURRENCY,
      revenueLabel:
        currentOrganization?.configuration?.currency === RETURN_CURRENCY ? "Return" : "Revenue",
    },
  } as ConfigData;

  const numberAbbreviator = new NumberAbbreviator(
    mergedConfig?.config?.currencySettings?.locale || "en-AU"
  );
  const currency = new Currency(numberAbbreviator, mergedConfig?.config?.currencySettings);

  const extendedConfig = useMemo(() => {
    return new Config(numberAbbreviator, currency, mergedConfig || undefined);
  }, [mergedConfig]);

  const value: ConfigContextType = {
    config: extendedConfig,
    loading,
  };

  return createElement(ConfigContext.Provider, { value }, children);
};
